import React, { useCallback, useState } from 'react'
import { useProjectAutofillDraftGuard } from '../../../../contexts/ProjectAutofillDraftGuardContext.tsx'
import type { ProjectAutofillDraftGuardApi } from '../../../../contexts/ProjectAutofillDraftGuardContext.tsx'
import styles from './ProjectAutofillDraftSendInterstitial.module.scss'

export function useProjectAutofillDraftSendGate() {
  const { getProjectAutofillDraftGuardApi } = useProjectAutofillDraftGuard()
  const [heldPayload, setHeldPayload] = useState<string | null>(null)

  const tryHoldSend = useCallback(
    (payload: string): boolean => {
      const api = getProjectAutofillDraftGuardApi()
      const dirty = Boolean(api?.isDirty())
      const hold = Boolean(payload && dirty)
      if (hold) {
        setHeldPayload(payload)
        return true
      }
      return false
    },
    [getProjectAutofillDraftGuardApi]
  )

  const dismiss = useCallback(() => {
    setHeldPayload(null)
  }, [])

  return { heldPayload, tryHoldSend, dismiss, getProjectAutofillDraftGuardApi }
}

type InterstitialProps = {
  open: boolean
  payload: string
  getProjectAutofillDraftGuardApi: () => ProjectAutofillDraftGuardApi | null
  onClearInputAndDismissGate: () => void
  onSendMessage: (message: string) => Promise<void>
  onCancel: () => void
}

export const ProjectAutofillDraftSendInterstitial: React.FC<InterstitialProps> = ({
  open,
  payload,
  getProjectAutofillDraftGuardApi,
  onClearInputAndDismissGate,
  onSendMessage,
  onCancel
}) => {
  const [saving, setSaving] = useState(false)

  if (!open) {
    return null
  }

  const api = getProjectAutofillDraftGuardApi()
  const canPersist = api?.canPersist() ?? false

  const finishSend = async () => {
    onClearInputAndDismissGate()
    await onSendMessage(payload)
  }

  const handleSaveAndSend = async () => {
    if (!api || !canPersist) return
    setSaving(true)
    try {
      const ok = await api.saveDraft()
      if (ok) {
        await finishSend()
      }
    } finally {
      setSaving(false)
    }
  }

  const handleSendWithoutSaving = async () => {
    await finishSend()
  }

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="project-autofill-draft-gate-title"
    >
      <div className={styles.panel}>
        <h2 id="project-autofill-draft-gate-title" className={styles.title}>
          Borrador sin guardar
        </h2>
        <p className={styles.body}>
          Tenes cambios en el borrador de obra que todavia no guardaste. Si envias el mensaje asi, el
          asistente puede usar una versión vieja del borrador.
        </p>
        {!canPersist && (
          <p className={styles.blocked}>
            No se puede guardar desde aca: revisa el borrador (por ejemplo confirmacion de rubro
            sugerido).
          </p>
        )}
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.primary}
            onClick={handleSaveAndSend}
            disabled={!canPersist || saving}
          >
            {saving ? 'Guardando...' : 'Guardar borrador y enviar'}
          </button>
          <button
            type="button"
            className={styles.secondary}
            onClick={handleSendWithoutSaving}
            disabled={saving}
          >
            Enviar sin guardar
          </button>
          <button type="button" className={styles.ghost} onClick={onCancel} disabled={saving}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  )
}
